'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface Metric {
  id: number;
  value: string;
  label: string;
  icon: string;
  color: string;
}

interface MetricCardProps {
  metric: Metric;
  isHydrated: boolean;
}

const MetricCard = ({ metric, isHydrated }: MetricCardProps) => {
  const [count, setCount] = useState(0);
  const targetValue = parseInt(metric.value.replace(/\D/g, ''));

  useEffect(() => {
    if (!isHydrated) return;

    let start = 0;
    const duration = 2000;
    const increment = targetValue / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= targetValue) {
        setCount(targetValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isHydrated, targetValue]);

  const displayValue = isHydrated ?
  metric.value.includes('+') ? `${count}+` : count.toString() :
  metric.value;

  return (
    <div className="flex flex-col items-center text-center p-8 bg-card rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-2">
      <div className={`w-16 h-16 ${metric.color} rounded-full flex items-center justify-center mb-4 shadow-hover`}>
        <Icon name={metric.icon as any} size={32} className="text-white" variant="solid" />
      </div>
      <p className="text-4xl lg:text-5xl font-heading font-bold text-primary mb-2">
        {displayValue}
      </p>
      <p className="text-base text-text-secondary font-body">
        {metric.label}
      </p>
    </div>);

};

const CommunityMetrics = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: Metric[] = [
  {
    id: 1,
    value: '500+',
    label: 'Active Members',
    icon: 'UserGroupIcon',
    color: 'bg-theme-orange'
  },
  {
    id: 2,
    value: '10',
    label: 'Years of Excellence',
    icon: 'TrophyIcon',
    color: 'bg-theme-red'
  },
  {
    id: 3,
    value: '15',
    label: 'Expert Coaches',
    icon: 'AcademicCapIcon',
    color: 'bg-success'
  },
  {
    id: 4,
    value: '1000+',
    label: 'Success Stories',
    icon: 'StarIcon',
    color: 'bg-gold'
  }];


  return (
    <section className="w-full py-16 lg:py-24 bg-background">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
            <p className="text-accent font-heading font-bold text-sm uppercase tracking-wider">
              Community Impact
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
            Building a Legacy of Excellence
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
            Since 2016, GO Speed Academy has transformed lives through disciplined training and community support.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {metrics.map((metric) =>
          <MetricCard key={metric.id} metric={metric} isHydrated={isHydrated} />
          )}
        </div>

        {/* GO Speed Family Photo */}
        <div className="mt-16 lg:mt-20">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
            <Image
              src="/assets/images/WhatsApp_Image_2024-08-16_at_11.55.53_PM-1770540493826.jpeg"
              alt="GO Speed Academy family - athletes and team members in uniforms celebrating together with raised hands, showcasing team spirit and unity"
              width={1200}
              height={800}
              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              priority={false} />

            <div className="absolute inset-0 bg-gradient-to-t from-theme-black/80 via-theme-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-0" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <h3 className="text-2xl lg:text-3xl font-heading font-bold mb-2">
                The GO Speed Family
              </h3>
              <p className="text-base lg:text-lg font-body opacity-90">
                United by passion, driven by excellence - our community of athletes and coaches
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CommunityMetrics;